import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StateContext } from '../../../../../StateProvider/index.js';
import Popup from '../../../../../Utilities/components/Popup';
import { uploadFileToS3, addFamilyMember } from '../../../../../Utilities/api/index.js';
import { BackButton } from '../../../../../Utilities/components/Buttons';
import dogBreeds from '../../../../../Utilities/data/dogBreeds.js';
import catBreeds from '../../../../../Utilities/data/catBreeds.js';
import './FamilyMemberAdd.css';

function FamilyMemberAdd() {
  var navigate = useNavigate();
  var ctx = useContext(StateContext) || {};
  var user = ctx.user;
  var refreshUserData = ctx.refreshUserData;

  // UI state
  var _useState1 = useState(null), uploadedImage = _useState1[0], setUploadedImage = _useState1[1];
  var _useState2 = useState(false), showPopup = _useState2[0], setShowPopup = _useState2[1];
  var _useState3 = useState(''), errorMsg = _useState3[0], setErrorMsg = _useState3[1];

  // Files
  var _useState4 = useState({ imageFile: null, vaccinationFile: null }), fileData = _useState4[0], setFileData = _useState4[1];

  // Form
  var _useState5 = useState({
    userId: '',            // we store UID here
    name: '',
    type: 'Human',         // default can be Human; user may switch to Dog/Cat
    breed: 'Not Specified',
    dob: '',               // HTML date value (YYYY-MM-DD) or ''
    vaccination: '',
    image: ''
  }), memberDetails = _useState5[0], setMemberDetails = _useState5[1];

  // Placeholders
  var placeholderImages = {
    Human: 'https://pawsible-s3.s3.us-west-1.amazonaws.com/user_files/defaultPerson.png',
    Dog: 'https://pawsible-s3.s3.us-west-1.amazonaws.com/user_files/defaultDog.png',
    Cat: 'https://pawsible-s3.s3.us-west-1.amazonaws.com/user_files/defaultCat.png'
  };
  function getPlaceholderImageUrl(type) {
    return placeholderImages[type] || placeholderImages.Human;
  }

  const getBreedOptions = () => {
      if (memberDetails.type === 'Dog') {
          return Object.keys(dogBreeds).sort().map(breed => (
              <option key={breed} value={breed}>{breed}</option>
          ));
      } else if (memberDetails.type === 'Cat') {
          return Object.keys(catBreeds).sort().map(breed => (
              <option key={breed} value={breed}>{breed}</option>
          ));
      } else {
          return <option value="Not Specified">Not Specified</option>;
      }
  };

  // Ensure userId is the UID (string), not numeric id
  useEffect(function () {
    if (user && user.uid) {
      setMemberDetails(function (prev) {
        var n = {};
        for (var k in prev) n[k] = prev[k];
        n.userId = user.uid;
        return n;
      });
    }
  }, [user]);

  // ---- helpers ----
  function onName(e) {
    var v = e && e.target ? e.target.value : '';
    setMemberDetails(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n.name = v; return n; });
  }
  function onType(e) {
    var v = e && e.target ? e.target.value : 'Human';
    setMemberDetails(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n.type = v; return n; });
  }
  function onBreed(e) {
    var v = e && e.target ? e.target.value : 'Not Specified';
    setMemberDetails(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n.breed = v; return n; });
  }
  function onDob(e) {
    var v = e && e.target ? e.target.value : '';
    setMemberDetails(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n.dob = v; return n; });
  }

  function handleImageChange(e) {
    var f = e && e.target && e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (!f) return;
    setFileData(function (prev) {
      var n = {};
      for (var k in prev) n[k] = prev[k];
      n.imageFile = f;
      return n;
    });
    try { setUploadedImage(URL.createObjectURL(f)); } catch (err) {}
  }

  function handleVaccinationUpload(e) {
    var f = e && e.target && e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (!f) return;
    setFileData(function (prev) {
      var n = {};
      for (var k in prev) n[k] = prev[k];
      n.vaccinationFile = f;
      return n;
    });
  }

  // returns YYYY-MM-DD or null
  function cleanDate(d) {
    if (!d) return null;
    var s = String(d);
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return null;
    var dt = new Date(s + 'T00:00:00Z');
    if (isNaN(dt.getTime())) return null;
    return m[1] + '-' + m[2] + '-' + m[3];
  }

  // ---- submit ----
  async function handleSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    setErrorMsg('');

    if (!memberDetails || !memberDetails.userId) { setErrorMsg('Missing user. Please re-login.'); return; }
    if (!memberDetails.name || !memberDetails.type) { setErrorMsg('Please enter name and type.'); return; }

    try {
      var imageUrl = memberDetails.image || '';
      if (fileData && fileData.imageFile) {
        imageUrl = await uploadFileToS3(fileData.imageFile);
      }
      if (!imageUrl) imageUrl = getPlaceholderImageUrl(memberDetails.type);

      var vaccinationUrl = memberDetails.vaccination || '';
      if (fileData && fileData.vaccinationFile) {
        vaccinationUrl = await uploadFileToS3(fileData.vaccinationFile);
      }

      // Build clean payload the API accepts
      var payload = {
        userId: memberDetails.userId,                      // UID string
        name: memberDetails.name,
        type: memberDetails.type,                          // 'Dog' | 'Cat' | 'Human'
        breed: memberDetails.breed || 'Not Specified',
        dob: cleanDate(memberDetails.dob),                 // null if empty/invalid
        vaccination: vaccinationUrl || null,
        image: imageUrl || null,
        status: 'incomplete'
      };

      // If Human, strip pet-ish fields as needed (harmless to leave, safe to normalize)
      if (payload.type === 'Human') {
        payload.breed = 'Not Specified';
        // If you prefer: payload.vaccination = null;
      }

      await addFamilyMember(payload);
      if (refreshUserData && user && user.uid) {
        await refreshUserData(user.uid);
      }
      setShowPopup(true);
    } catch (error) {
      var msg = 'Error adding family member.';
      try {
        if (error && error.response && error.response.data) {
          var data = error.response.data;
          if (typeof data === 'string') msg = data;
          else if (data.error) msg = String(data.error);
          else if (data.message) msg = String(data.message);
        } else if (error && error.message) {
          msg = String(error.message);
        }
      } catch (e2) {}
      setErrorMsg(msg);
      // console.error('Failed to add family member:', error);
    }
  }

  function handleClosePopup() {
    setShowPopup(false);
    navigate('/profile');
  }

  // ---- UI ----
  return (
    <div className="family-member-edit">
      <Popup isOpen={showPopup} handleClose={handleClosePopup}>
        <p>New family member has been added successfully!</p>
        <div className='family-member-btn-container'>
            <button className='family-member-ok-btn' onClick={handleClosePopup}>OK</button>
        </div>
      </Popup>

      <div className="family-member-edit-header">
        <BackButton />
      </div>

      <div className="container">
        {errorMsg ? <div className="fm-alert">{errorMsg}</div> : null}

        <form onSubmit={handleSubmit}>
          <div className="image-upload-container">
            <img
              className="profile-image"
              src={uploadedImage || getPlaceholderImageUrl(memberDetails.type)}
              alt={(memberDetails.type || 'Human') + ' Placeholder'}
            />
            <label htmlFor="image-upload" className="image-upload-label">
              <input type="file" id="image-upload" className="image-upload" onChange={handleImageChange} />
            </label>
          </div>

          <label htmlFor="name">Name</label>
          <input type="text" id="name" value={memberDetails.name} onChange={onName} />

          <label htmlFor="type">Type</label>
          <select id="type" value={memberDetails.type} onChange={onType}>
            <option value="Human">Human</option>
            <option value="Dog">Dog</option>
            <option value="Cat">Cat</option>
          </select>

          {memberDetails.type !== 'Human' ? (
            <div>
              <label htmlFor="breed">Breed</label>
              <select id="breed" value={memberDetails.breed} onChange={onBreed}>
                  <option value="Not Specified">Not Specified</option>
                  {getBreedOptions()}
              </select>

              <label htmlFor="dob">Date of Birth</label>
              <input type="date" id="dob" value={memberDetails.dob} onChange={onDob} />

              <div className="vaccination-container">
                <label htmlFor="vaccination">Vaccination Records</label>
                <input type="file" id="vaccination" onChange={handleVaccinationUpload} />
                <label htmlFor="vaccination-message" className="vaccination-hint">
                  * Records will be verified after upload.
                </label>
              </div>
            </div>
          ) : null}

          <input type="submit" value="Add Family Member" />
        </form>
      </div>
    </div>
  );
}

export default FamilyMemberAdd;
